import { Component, OnDestroy, OnInit, WritableSignal, inject, signal } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, combineLatest, debounceTime, interval, map, of, shareReplay, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { AccountPayment } from '../../models/payment.model';
import { AuthService } from '../../services/auth.service';
import { AccountContextService } from '../../services/account-context.service';
import { FirestoreService } from '../../services/firestore.service';
import { StripeCheckoutStatusSyncResponse, StripeCheckoutService } from '../../services/stripe-checkout.service';
import { SubscriptionPlansService } from '../../services/subscription-plans.service';
import { SubscriptionPlan } from '../../models/subscription-plan.model';

@Component({
  selector: 'app-subscription-confirmation',
  templateUrl: './subscription-confirmation.component.html',
  styleUrl: './subscription-confirmation.component.css'
})
export class SubscriptionConfirmationComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly firestoreService = inject(FirestoreService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly accountContextService = inject(AccountContextService);
  private readonly stripeCheckoutService = inject(StripeCheckoutService);
  private readonly subscriptionPlansService = inject(SubscriptionPlansService);
  private selectedPlanDetails: SubscriptionPlan | null = null;
  private readonly destroy$ = new Subject<void>();
  private readonly navigationState = this.router.getCurrentNavigation()?.extras?.state as {
    selectedPlanLevel?: string;
    paymentId?: string;
    subscriptionId?: string;
  } | null;
  private readonly historyState = history.state as {
    selectedPlanLevel?: string;
    paymentId?: string;
    subscriptionId?: string;
  } | null;

  readonly selectedPlanLevel = this.route.snapshot.queryParamMap.get('selectedPlanLevel')
    ?? this.navigationState?.selectedPlanLevel
    ?? this.historyState?.selectedPlanLevel
    ?? '';
  readonly paymentId = this.route.snapshot.queryParamMap.get('paymentId')
    ?? this.navigationState?.paymentId
    ?? this.historyState?.paymentId
    ?? '';
  readonly subscriptionId = this.route.snapshot.queryParamMap.get('subscriptionId')
    ?? this.navigationState?.subscriptionId
    ?? this.historyState?.subscriptionId
    ?? '';

  private readonly checkoutSyncUnavailable: WritableSignal<boolean> = signal(false);

  readonly payment$ = combineLatest([
    this.accountContextService.activeAccountId$,
    interval(4000).pipe(startWith(0))
  ]).pipe(
    switchMap(([accountId]) => {
      if (!accountId || !this.paymentId) {
        return of(null);
      }
      return this.firestoreService.getAccountPayment(accountId, this.paymentId);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly profile$ = this.authService.user$.pipe(
    switchMap(user => {
      if (!user) {
        return of(null);
      }
      return this.firestoreService.getUserProfile(user.uid);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly subscription$ = interval(4000).pipe(
    startWith(0),
    switchMap(() => {
      if (!this.subscriptionId) {
        return of(null);
      }
      return this.firestoreService.getSubscription(this.subscriptionId);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );


  readonly checkoutSync$ = combineLatest([
    this.accountContextService.activeAccountId$,
    this.authService.user$,
    interval(5000).pipe(startWith(0))
  ]).pipe(
    debounceTime(250),
    switchMap(([accountId, user]) => {
      if (!accountId || !user?.uid || !this.paymentId || this.checkoutSyncUnavailable()) {
        return of(null as StripeCheckoutStatusSyncResponse | null);
      }
      return this.stripeCheckoutService.syncCheckoutStatus({
        accountId,
        userId: user.uid,
        paymentId: this.paymentId,
        subscriptionId: this.subscriptionId || null
      }).pipe(
        catchError(error => {
          const status = typeof error?.status === 'number' ? error.status : 0;
          if (status === 404 || status === 405) {
            this.checkoutSyncUnavailable.set(true);
          }
          return of(null);
        })
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly status$ = combineLatest([
    this.payment$.pipe(startWith(null)),
    this.profile$.pipe(startWith(null)),
    this.subscription$.pipe(startWith(null)),
    this.checkoutSync$.pipe(startWith(null))
  ]).pipe(
    map(([payment, profile, subscription, checkoutSync]) => {
      const paymentStatus = (payment?.status ?? '').toLowerCase();
      const userPaymentStatus = (profile?.paymentStatus ?? '').toLowerCase();
      const subscriptionStatus = (subscription?.['status'] as string | undefined ?? '').toLowerCase();
      const syncedPaymentStatus = (checkoutSync?.paymentStatus ?? '').toLowerCase();
      const syncedSubscriptionStatus = (checkoutSync?.stripeSubscriptionStatus ?? '').toLowerCase();

      const confirmedByWebhook = paymentStatus === 'confirmed' ||
        paymentStatus === 'paid' ||
        userPaymentStatus === 'active' ||
        subscriptionStatus === 'active' ||
        syncedPaymentStatus === 'active' ||
        syncedSubscriptionStatus === 'active' ||
        syncedSubscriptionStatus === 'trialing';
      const failed = paymentStatus === 'failed' ||
        paymentStatus === 'canceled' ||
        userPaymentStatus === 'past_due' ||
        userPaymentStatus === 'canceled' ||
        subscriptionStatus === 'past_due' ||
        subscriptionStatus === 'canceled' ||
        syncedPaymentStatus === 'past_due' ||
        syncedPaymentStatus === 'canceled' ||
        syncedSubscriptionStatus === 'past_due' ||
        syncedSubscriptionStatus === 'canceled';

      if (failed) {
        return 'failed';
      }
      if (confirmedByWebhook) {
        return 'confirmed';
      }
      return 'pending';
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly paymentSignal = toSignal(this.payment$, { initialValue: null as AccountPayment | null });
  readonly statusSignal = toSignal(this.status$, { initialValue: 'pending' as 'pending' | 'confirmed' | 'failed' });

  countdown = 5;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private profileSyncCompleted = false;

  ngOnInit(): void {
    if (!this.selectedPlanLevel) {
      this.router.navigate(['/subscriptions']);
      return;
    }

    this.subscriptionPlansService.getPlanByLevel(this.selectedPlanLevel)
      .pipe(takeUntil(this.destroy$))
      .subscribe(plan => {
        this.selectedPlanDetails = plan ?? null;
      });

    this.status$.pipe(takeUntil(this.destroy$)).subscribe(status => {
      if (status === 'confirmed' && !this.countdownInterval) {
        this.startCountdown();
      }
    });

    combineLatest([this.status$, this.authService.user$, this.payment$, this.subscription$, this.checkoutSync$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([status, user, payment, subscription, checkoutSync]) => {
        if (
          status !== 'confirmed' ||
          !user?.uid ||
          this.profileSyncCompleted ||
          !this.hasCurrentCheckoutProof(payment, subscription, checkoutSync)
        ) {
          return;
        }

        const patch = this.buildSubscriptionProfilePatch(subscription ?? {}, payment);
        if (!patch) {
          this.profileSyncCompleted = true;
          return;
        }

        this.firestoreService.updateUserProfile(user.uid, patch)
          .then(() => {
            this.profileSyncCompleted = true;
          })
          .catch(error => {
            console.error('Failed to sync subscription profile fields after checkout:', error);
          });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  private startCountdown(): void {
    this.countdownInterval = setInterval(() => {
      if (this.countdown > 0) {
        this.countdown--;
        return;
      }
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
      this.router.navigate(['/dashboard']);
    }, 1000);
  }

  private buildSubscriptionProfilePatch(
    subscription: Record<string, unknown>,
    payment: AccountPayment | null
  ): Partial<Record<string, unknown>> | null {
    const normalizedLevel = this.asString(
      subscription['planId'],
      subscription['planLevel'],
      subscription['subscriptionLevel'],
      payment?.planLevel,
      this.selectedPlanLevel
    )?.toLowerCase();
    const normalizedName = this.asString(
      subscription['planName'],
      subscription['subscriptionName'],
      payment?.planName,
      this.selectedPlanDetails?.name
    );
    const currentPeriodEnd = this.asDateValue(
      subscription['currentPeriodEnd'],
      subscription['current_period_end'],
      subscription['expiresAt'],
      subscription['expires_at']
    );
    const subscriptionMaxBoats = this.asNumber(
      subscription['maxBoatsAllowed'],
      subscription['max_boats_allowed']
    );
    const resolvedMaxBoats = subscriptionMaxBoats ?? this.selectedPlanDetails?.maxBoatsAllowed ?? null;

    const patch: Partial<Record<string, unknown>> = {
      paymentStatus: 'active',
      isSubscriptionActive: true
    };

    if (normalizedLevel) {
      patch['subscriptionLevel'] = normalizedLevel;
    }
    if (normalizedName) {
      patch['subscriptionName'] = normalizedName;
    }
    if (currentPeriodEnd) {
      patch['currentPeriodEnd'] = currentPeriodEnd;
      patch['expiresAt'] = currentPeriodEnd;
    }
    if (resolvedMaxBoats !== null && resolvedMaxBoats !== undefined) {
      patch['maxBoatsAllowed'] = resolvedMaxBoats;
    }
    if (this.subscriptionId) {
      patch['latestSubscriptionId'] = this.subscriptionId;
    }

    return Object.keys(patch).length > 0 ? patch : null;
  }

  private asNumber(...values: unknown[]): number | null {
    for (const value of values) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
      }
      if (typeof value === 'string' && value.trim().length > 0) {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
    }
    return null;
  }

  private hasCurrentCheckoutProof(
    payment: AccountPayment | null,
    subscription: Record<string, unknown> | null,
    checkoutSync: StripeCheckoutStatusSyncResponse | null
  ): boolean {
    const paymentStatus = (payment?.status ?? '').toLowerCase();
    if (paymentStatus === 'confirmed' || paymentStatus === 'paid') {
      return true;
    }

    const syncedPaymentStatus = (checkoutSync?.paymentStatus ?? '').toLowerCase();
    const syncedSubscriptionStatus = (checkoutSync?.stripeSubscriptionStatus ?? '').toLowerCase();
    if (
      syncedPaymentStatus === 'active' ||
      syncedSubscriptionStatus === 'active' ||
      syncedSubscriptionStatus === 'trialing'
    ) {
      return true;
    }

    if (!this.subscriptionId || !subscription) {
      return false;
    }

    const subscriptionStatus = this.asString(subscription['status'])?.toLowerCase();
    const subscriptionPaymentStatus = this.asString(subscription['paymentStatus'])?.toLowerCase();
    return subscriptionStatus === 'active' ||
      subscriptionStatus === 'trialing' ||
      subscriptionPaymentStatus === 'active';
  }

  private asString(...values: unknown[]): string | null {
    for (const value of values) {
      if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
      }
    }
    return null;
  }

  private asDateValue(...values: unknown[]): Date | Timestamp | null {
    for (const value of values) {
      if (!value) {
        continue;
      }
      if (value instanceof Date || value instanceof Timestamp) {
        return value;
      }
      if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
        return value as Timestamp;
      }
      if (typeof value === 'number') {
        return new Date(value);
      }
      if (typeof value === 'string') {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) {
          return parsed;
        }
      }
    }
    return null;
  }
}
